import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { UserX } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  selectDeletionRequests,
  selectDeletionRequestsStatus,
} from "../../features/deletion-requests/deletion-requests-slice";
import {
  dismissDeletionRequest,
  eraseDeletionRequest,
  fetchDeletionRequests,
} from "../../features/deletion-requests/slice-extensions/deletion-requests-slice-extension";
import { fetchErasurePreview } from "../../features/members/slice-extensions/member-erasure-slice-extension";
import {
  erasureToastText,
  joinSentences,
  trainingHoldSentences,
} from "../../features/members/utils/trainingHoldText";

/**
 * Inbox section: members (or guardians) who asked for their data to be deleted.
 * Erase runs the GDPR tombstone; dismiss restores the member's portal access.
 */
const DeletionRequestsList = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const requests = useAppSelector(selectDeletionRequests);
  const status = useAppSelector(selectDeletionRequestsStatus);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchDeletionRequests({}));
  }, [dispatch]);

  const handleErase = async (requestId: string, memberId: string, memberName: string) => {
    // The confirmation names any training record the erasure keeps under legal hold (PD-0021).
    const preview = await dispatch(fetchErasurePreview(memberId));
    const kept = fetchErasurePreview.fulfilled.match(preview) ? preview.payload : [];
    const confirmText = joinSentences([
      t("deletionRequests.eraseConfirm", { name: memberName }),
      trainingHoldSentences(t, kept, i18n.language, "members.dataRights.trainingHeld"),
    ], i18n.language);
    if (!window.confirm(confirmText)) {
      return;
    }
    setBusyId(requestId);
    try {
      const erased = await dispatch(eraseDeletionRequest(requestId)).unwrap();
      toast.success(erasureToastText(t, erased, i18n.language, "deletionRequests.erasedToast"));
    } catch (error) {
      toast.error((error as { message?: string })?.message || t("deletionRequests.failed", "Something went wrong"));
    } finally {
      setBusyId(null);
    }
  };

  if (status !== "failed" && requests.length === 0) {
    return null;
  }

  return (
    <section className="mt-8">
      <h2 className="flex items-center gap-2 text-lg font-semibold text-grey-20 dark:text-white">
        <UserX size={20} />
        {t("deletionRequests.title", "Deletion requests")}
      </h2>
      <p className="mt-1 text-sm text-grey-40 dark:text-grey-80">
        {t(
          "deletionRequests.subtitle",
          "These members asked for their data to be deleted. Erasing keeps billing records and tombstones the personal data.",
        )}
      </p>

      <ul className="mt-3 flex flex-col gap-2">
        {requests.map((request) => {
          const name = `${request.memberFirstName} ${request.memberLastName}`.trim();
          const pending = request.status === "Pending";
          return (
            <li
              key={request.id}
              className="with-border flex flex-wrap items-center justify-between gap-3 rounded-xl bg-white p-3 dark:bg-primary-20"
            >
              <div className="min-w-0">
                <p className="font-medium text-grey-20 dark:text-white">{name}</p>
                <p className="text-xs text-grey-40 dark:text-grey-80">
                  {new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" }).format(
                    new Date(request.dateCreated),
                  )}
                  {request.requestedByGuardian
                    ? ` · ${t("deletionRequests.byGuardian", "requested by guardian")}`
                    : ""}
                  {!pending ? ` · ${request.status === "Erased"
                    ? t("deletionRequests.statusErased", "Erased")
                    : t("deletionRequests.statusDismissed", "Dismissed")}` : ""}
                </p>
              </div>
              {pending && (
                <div className="flex shrink-0 items-center gap-2">
                  <button
                    type="button"
                    className="btn btn-secondary text-red-600 dark:text-red-400"
                    disabled={busyId !== null}
                    onClick={() => handleErase(request.id, request.memberId, name)}
                  >
                    {t("deletionRequests.erase", "Erase data")}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={busyId !== null}
                    onClick={() => dispatch(dismissDeletionRequest(request.id))}
                  >
                    {t("deletionRequests.dismiss", "Dismiss")}
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default DeletionRequestsList;
